import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, firestore } from '../../firebaseConfig';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useToast } from '../../context/ToastContext';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon, HomeIcon, KeyIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import CrispChat from '../../utils/crispChat';

const ActionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'form'
  const [mode, setMode] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');


  useEffect(() => {
    const handleEmailVerification = async (oobCode) => {
      try {
        await applyActionCode(auth, oobCode);
        
        // Update user document to trigger cross-tab sync
        if (auth.currentUser) {
          try {
            const userDoc = doc(firestore, 'users', auth.currentUser.uid);
            await setDoc(userDoc, {
              email_verified: true,
            }, { merge: true });
          } catch (error) {
            console.error('Error updating user document:', error);
          }
        }
        
        setStatus('success');
        showToast('Your email has been successfully verified!', 'success');
      } catch (error) {
        setStatus('error');
        
        let errorMsg = '';
        switch (error.code) {
          case 'auth/expired-action-code':
            errorMsg = 'This verification link has expired. Please request a new one.';
            break;
          case 'auth/invalid-action-code':
            errorMsg = 'This verification link is invalid or has already been used.';
            break;
          case 'auth/user-disabled':
            errorMsg = 'Your account has been disabled. Please contact support.';
            break;
          default:
            errorMsg = 'An error occurred while verifying your email. Please try again.';
        }
        
        setErrorMessage(errorMsg);
        showToast(errorMsg, 'error');
      }
    };

    const handlePasswordResetVerification = async (oobCode) => {
      try {
        // Verify the password reset code is valid
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmail(email);
        setStatus('form');
      } catch (error) {
        setStatus('error');
        
        let errorMsg = '';
        switch (error.code) {
          case 'auth/expired-action-code':
            errorMsg = 'This password reset link has expired. Please request a new one.';
            break;
          case 'auth/invalid-action-code':
            errorMsg = 'This password reset link is invalid or has already been used.';
            break;
          default:
            errorMsg = 'An error occurred while verifying the reset link. Please try again.';
        }
        
        setErrorMessage(errorMsg);
        showToast(errorMsg, 'error');
      }
    };

    const handleAction = async () => {
      const actionMode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      
      setMode(actionMode);

      if (!actionMode || !oobCode) {
        setStatus('error');
        setErrorMessage('Invalid action link. Please check your email for the correct link.');
        return;
      }

      switch (actionMode) {
        case 'verifyEmail':
          await handleEmailVerification(oobCode);
          break;
        case 'resetPassword':
          await handlePasswordResetVerification(oobCode);
          break;
        default:
          setStatus('error');
          setErrorMessage('Unknown action type. Please contact support.');
      }
    };

    handleAction();
  }, [searchParams, showToast]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }
    
    if (newPassword.length < 6) {
      showToast('Password must be at least 6 characters long', 'error');
      return;
    }
    
    setIsSubmitting(true);
    const oobCode = searchParams.get('oobCode');
    
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus('success');
      showToast('Your password has been successfully reset!', 'success');
    } catch (error) {
      let errorMsg = 'Failed to reset password. Please try again.';
      if (error.code === 'auth/weak-password') {
        errorMsg = 'Password is too weak. Please choose a stronger password.';
      }
      setErrorMessage(errorMsg);
      showToast(errorMsg, 'error');
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (mode === 'verifyEmail' && status === 'success') {
      navigate('/dashboard');
    } else if (mode === 'resetPassword' && status === 'success') {
      navigate('/user/login');
    } else if (auth.currentUser) {
      navigate('/user/settings');
    } else {
      navigate('/user/login');
    }
  };

  const handleContactSupport = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
    }
  };

  const getIcon = () => {
    if (status === 'loading') {
      return <ArrowPathIcon className="w-32 h-32 text-content-primary animate-spin drop-shadow-2xl" />;
    }
    if (status === 'success') {
      return <CheckCircleIcon className="w-32 h-32 text-accent drop-shadow-2xl" />;
    }
    if (status === 'error') {
      return <XCircleIcon className="w-32 h-32 text-error drop-shadow-2xl" />;
    }
    if (status === 'form' && mode === 'resetPassword') {
      return <KeyIcon className="w-32 h-32 text-primary drop-shadow-2xl" />;
    }
    return <EnvelopeIcon className="w-32 h-32 text-primary drop-shadow-2xl" />;
  };

  const getTitle = () => {
    if (status === 'loading') {
      return mode === 'verifyEmail' ? 'Verifying Your Email' : 'Verifying Reset Link';
    }
    if (status === 'success') {
      return mode === 'verifyEmail' ? 'Email Verified!' : 'Password Reset!';
    }
    if (status === 'error') {
      return mode === 'verifyEmail' ? 'Verification Failed' : 'Reset Failed';
    }
    if (status === 'form' && mode === 'resetPassword') {
      return 'Reset Your Password';
    }
    return 'Processing...';
  };

  const getJapaneseTitle = () => {
    if (status === 'loading') {
      return mode === 'verifyEmail' ? 'メールアドレスを確認中' : 'リンクを確認中';
    }
    if (status === 'success') {
      return mode === 'verifyEmail' ? 'メールアドレスが確認されました' : 'パスワードがリセットされました';
    }
    if (status === 'error') {
      return mode === 'verifyEmail' ? '確認に失敗しました' : 'リセットに失敗しました';
    }
    if (status === 'form' && mode === 'resetPassword') {
      return 'パスワードをリセット';
    }
    return '処理中...';
  };

  return (
    <>
      <CrispChat />
      <main className="min-h-screen bg-background-primary flex flex-col items-center justify-center text-center p-4 relative overflow-hidden">
        {/* Background Pattern with Floating Bubbles */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 rounded-full animate-float-up"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              width: '120px', height: '120px',
              animationDelay: '0s', animationDuration: '8s',
            }}
          />
          <div className="absolute top-1/3 right-1/4 rounded-full animate-float-up"
            style={{
              background: 'linear-gradient(135deg, var(--color-accent), var(--color-primary))',
              width: '80px', height: '80px',
              animationDelay: '2s', animationDuration: '10s',
            }}
          />
          <div className="absolute bottom-1/4 left-1/3 rounded-full animate-float-up"
            style={{
              background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))',
              width: '100px', height: '100px',
              animationDelay: '4s', animationDuration: '12s',
            }}
          />
          <div className="absolute bottom-1/3 right-1/3 rounded-full animate-float-up"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
              width: '60px', height: '60px',
              animationDelay: '6s', animationDuration: '9s',
            }}
          />
        </div>

        {/* Main Content */}
        <div className="relative max-w-lg w-full animate-slide-in-center">
          {/* Status Icon with enhanced glow */}
          <div className="relative mb-8">
            <div className="absolute inset-0 rounded-full blur-3xl opacity-40"
              style={{
                background: status === 'loading' 
                  ? 'radial-gradient(circle, var(--color-primary), var(--color-secondary), transparent 70%)'
                  : status === 'success'
                  ? 'radial-gradient(circle, var(--color-accent), var(--color-primary), transparent 70%)'
                  : status === 'error'
                  ? 'radial-gradient(circle, var(--color-error), var(--color-secondary), transparent 70%)'
                  : 'radial-gradient(circle, var(--color-primary), var(--color-accent), transparent 70%)',
                transform: 'scale(1.2)',
              }}
            />
            <div className="relative w-48 h-48 mx-auto flex items-center justify-center">
              {getIcon()}
            </div>
          </div>

          {/* Headlines */}
          <div className="mb-6">
            <h1 className="text-4xl sm:text-5xl font-black text-content-primary mb-3 drop-shadow-sm">
              {getTitle()}
            </h1>
            <p className="text-2xl sm:text-3xl font-bold text-content-primary opacity-80 drop-shadow-sm" lang="ja">
              {getJapaneseTitle()}
            </p>
          </div>

          {/* Content based on status */}
          {status === 'form' && mode === 'resetPassword' ? (
            // Password Reset Form
            <form onSubmit={handlePasswordReset} className="space-y-4 mb-8">
              <div className="text-left">
                <p className="text-sm text-content-secondary mb-4">
                  Enter a new password for {email}
                </p>
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-content-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-content-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 rounded-2xl font-bold text-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            // Status Messages
            <div className="mb-10">
              {status === 'loading' && (
                <>
                  <p className="text-lg text-content-secondary opacity-90 mb-2 drop-shadow-sm">
                    Please wait while we process your request...
                  </p>
                  <p className="text-base text-content-secondary opacity-80 drop-shadow-sm" lang="ja">
                    リクエストを処理しています。お待ちください...
                  </p>
                </>
              )}
              
              {status === 'success' && (
                <>
                  <p className="text-lg text-content-secondary opacity-90 mb-2 drop-shadow-sm">
                    {mode === 'verifyEmail' 
                      ? 'Your email has been successfully verified.'
                      : 'Your password has been successfully reset.'}
                  </p>
                  <p className="text-base text-content-secondary opacity-80 drop-shadow-sm" lang="ja">
                    {mode === 'verifyEmail'
                      ? 'これでKikikataの全機能をご利用いただけます。'
                      : '新しいパスワードでログインできます。'}
                  </p>
                </>
              )}
              
              {status === 'error' && (
                <>
                  <p className="text-lg text-content-secondary opacity-90 mb-2 drop-shadow-sm">
                    {errorMessage}
                  </p>
                  <p className="text-base text-content-secondary opacity-80 drop-shadow-sm" lang="ja">
                    お困りの場合はサポートまでお問い合わせください。
                  </p>
                </>
              )}
            </div>
          )}

          {/* Action Button */}
          {(status !== 'loading' && status !== 'form') && (
            <div className="relative">
              <button
                onClick={handleContinue}
                className="inline-flex items-center px-8 py-4 rounded-2xl font-bold text-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1 active:scale-95"
                style={{
                  backgroundColor: status === 'success' ? 'var(--color-accent)' : 'var(--color-primary)',
                }}
              >
                <HomeIcon className="h-6 w-6 mr-3 drop-shadow-sm" />
                <span className="text-lg drop-shadow-sm">
                  {status === 'success' && mode === 'verifyEmail' && 'Continue to Dashboard'}
                  {status === 'success' && mode === 'resetPassword' && 'Go to Login'}
                  {status === 'error' && auth.currentUser && 'Go to Settings'}
                  {status === 'error' && !auth.currentUser && 'Go to Login'}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Contact Support */}
        <div className="relative mt-8 text-center">
          <p className="text-sm text-content-secondary opacity-80">
            Having trouble? Contact us at{' '}
            <a 
              href="mailto:contact@kikikata.app" 
              className="text-primary hover:text-primary-dark transition-colors"
            >
              contact@kikikata.app
            </a>
            {' '}or{' '}
            <button
              onClick={handleContactSupport}
              className="text-primary hover:text-primary-dark transition-colors underline"
            >
              chat with us
            </button>
          </p>
        </div>

        {/* Additional floating elements for atmosphere */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-10 left-10 w-3 h-3 rounded-full opacity-60 animate-float-up"
            style={{ background: 'var(--color-primary)', animationDelay: '1s', animationDuration: '6s' }}
          />
          <div className="absolute top-20 right-20 w-2 h-2 rounded-full opacity-40 animate-float-up"
            style={{ background: 'var(--color-secondary)', animationDelay: '3s', animationDuration: '8s' }}
          />
          <div className="absolute bottom-20 left-20 w-4 h-4 rounded-full opacity-50 animate-float-up"
            style={{ background: 'var(--color-accent)', animationDelay: '5s', animationDuration: '10s' }}
          />
          <div className="absolute bottom-10 right-10 w-2 h-2 rounded-full opacity-30 animate-float-up"
            style={{ background: 'var(--color-primary)', animationDelay: '7s', animationDuration: '12s' }}
          />
        </div>
      </main>
    </>
  );
};

export default ActionPage;